import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  CellSelectionOptions,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, LinearProgress, Typography, Button } from "@mui/material";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import WithAuth from "../../Hoc/WithAuth";
import {
  AddTechnologyResponse,
  getAllTechnologiesResponse,
  Technology
} from "../../Graphql/types/technology";
import ViewTechDetailButton from "./ViewTechDetailButton";
import {
  addTechnologyQuery,
  getAllTechnologiesQuery
} from "../../Graphql/querries/technologyQuery";
import EditTechnologyDialog from "./EditTechnologyDialog";
import { TECHNOLOGIES } from "../Routes";

ModuleRegistry.registerModules([AllCommunityModule]);

const TechnologyList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<Technology[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const [searchParams] = useSearchParams();
  const [openAddTech, setOpenAddTech] = useState(false);
  const navigate = useNavigate();

  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<getAllTechnologiesResponse, Error>({
    mutationKey: ["getTechnologyList"],
    mutationFn: () => fetchGraphQL(getAllTechnologiesQuery, {}, setAlert),
    onSuccess: (response) => {
      setRowData(response.getAllTechnologies || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
    return { handle: { mode: "range" } };
  }, []);

  useEffect(() => {
    setColumnDefs([
      { field: "id", headerName: "id" },
      { field: "title", headerName: "Title" },
      { field: "description", headerName: "Descriptions", minWidth: 250 },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: ViewTechDetailButton
      }
    ]);
  }, []);

  useEffect(() => {
    mutate();
  }, [searchParams]);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  const { mutate: addTechnology, isPending: addingTech } = useMutation<
    AddTechnologyResponse,
    Error,
    Technology
  >({
    mutationKey: ["addTechnology"],
    mutationFn: (input) =>
      fetchGraphQL(addTechnologyQuery, { input }, setAlert),
    onSuccess: (response) => {
      setOpenAddTech(false);
      navigate(`${TECHNOLOGIES}/${response.addTechnology.id}`);
    }
  });

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  const handleAddTech = (tech: Technology): void => {
    addTechnology({ ...tech, technologyItems: [] });
  };

  return (
    <div style={containerStyle}>
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Technology Listing
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "right",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setOpenAddTech(true);
          }}
        >
          Add new Technology
        </Button>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          cellSelection={cellSelection}
          getRowId={getRowId}
        />
      </div>
      {openAddTech && (
        <EditTechnologyDialog
          open={openAddTech}
          onClose={() => setOpenAddTech(false)}
          technology={{} as Technology}
          onSave={handleAddTech}
          isUpdating={addingTech}
        />
      )}
    </div>
  );
};

export default WithAuth(TechnologyList);
